import React, {type JSX, useEffect, useRef} from 'react';
import {Popup, Ref, type StrictPopupProps} from 'semantic-ui-react';

import {scrollIntoBounds} from '../../util/flash';
import * as S from './PersistentFlasher.styles';

const DEFAULT_STYLE: React.CSSProperties = {};

interface PersistentFlasherProps {
  style?: React.CSSProperties;
  visible: boolean;
  trigger: JSX.Element;
  offsetX?: number;
  offsetY?: number;
  scrollIntoView?: boolean; // if true, the window will autoscroll to the trigger
  minSpaceFromTop?: number; // used if scrollIntoView is true
  minSpaceFromBottom?: number; // used if scrollIntoView is true
  popupClassName?: string;
  popupContent?: string | JSX.Element;
  popupPosition?: StrictPopupProps['position'];
  popupOffsetX?: number;
  popupOffsetY?: number;
}

// This adds a flashing blue dot to the trigger component
// Use it to draw the user's attention to a button, for example
export const PersistentFlasher: React.FC<PersistentFlasherProps> = ({
  style = DEFAULT_STYLE,
  trigger,
  visible,
  offsetX = 0,
  offsetY = 0,
  scrollIntoView = false,
  minSpaceFromTop = 20,
  minSpaceFromBottom = 20,
  popupClassName,
  popupContent,
  popupPosition = 'bottom center',
  popupOffsetX = 0,
  popupOffsetY = 10,
}) => {
  const triggerRef = useRef<HTMLElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || rootRef.current == null) {
      return;
    }
    if (scrollIntoView) {
      const rect = rootRef.current.getBoundingClientRect();
      scrollIntoBounds(rect, minSpaceFromTop, minSpaceFromBottom);
    }
  }, [visible, scrollIntoView, minSpaceFromTop, minSpaceFromBottom]);

  const bluedot = (
    <S.BlueDot
      onClick={() => {
        triggerRef.current?.click();
      }}
      style={{
        left: offsetX,
        top: `calc(50% + ${offsetY}px)`,
        ...style,
      }}
    />
  );

  return (
    <S.Root ref={rootRef}>
      <Ref innerRef={triggerRef}>{trigger}</Ref>
      {visible &&
        (popupContent == null ? (
          bluedot
        ) : (
          <Popup
            className={popupClassName}
            trigger={bluedot}
            content={popupContent}
            position={popupPosition}
            size="tiny"
            offset={`${popupOffsetX}px, ${popupOffsetY}px`}
          />
        ))}
    </S.Root>
  );
};
